import {
  getHomePage,
  getPageRoutes,
  upsertPageRoute,
  type PuckRoute,
} from "@/api/cms-puck/api";
import { SlugPathComposer } from "@/components/cms-puck/editor/SlugPathComposer";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import type { SupportedLocale } from "@/i18n";
import { slugify } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Home, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cmsNew, useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

/**
 * Éditeur SEO d'une page CMS avancé — miroir du SEO Galadmin (titre / description /
 * mots-clés) mais avec UN sélecteur de langue (au lieu d'une section par langue).
 * Édite aussi le slug par langue (porté par `PuckPageRoute`). L'état « page
 * d'accueil » n'est ici qu'affiché (badge + slug verrouillé) : il se définit /
 * retire depuis le menu ⋯ de la liste des pages (`pages/cms/advanced.tsx`).
 */

const homeKey = ["cms-puck", "home"] as const;
const routesKey = (pageId: string) => ["cms-puck", "routes", pageId] as const;

interface RouteDraft {
  slug: string;
  title: string;
  description: string;
  keywords: string;
}

const emptyDraft = (): RouteDraft => ({
  slug: "",
  title: cmsNew("cms.new.auto.title.d41d8cd9", ""),
  description: cmsNew("cms.new.auto.description.d41d8c", ""),
  keywords: "",
});

export interface SeoEditorProps {
  pageId: string;
  pageName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SeoEditor({
  pageId,
  pageName,
  open,
  onOpenChange,
}: SeoEditorProps) {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { websiteLanguages, defaultWebsiteLanguage, isLoading: langsLoading } =
    useCmsWebsiteLanguages();

  const routesQuery = useQuery({
    queryKey: routesKey(pageId),
    queryFn: () => getPageRoutes(pageId),
    enabled: open && !!pageId,
  });

  const homeQuery = useQuery({
    queryKey: homeKey,
    queryFn: getHomePage,
    enabled: open,
  });
  const isHome = homeQuery.data?.homePageId === pageId;

  // Langue actuellement éditée.
  const [lang, setLang] = useState<SupportedLocale>(defaultWebsiteLanguage);
  useEffect(() => {
    if (websiteLanguages.length && !websiteLanguages.includes(lang)) {
      setLang(defaultWebsiteLanguage);
    }
  }, [websiteLanguages, defaultWebsiteLanguage, lang]);

  // Brouillon local par langue, réhydraté au chargement des routes.
  const [drafts, setDrafts] = useState<Record<string, RouteDraft>>({});
  useEffect(() => {
    if (!routesQuery.data) return;
    const byLang = new Map<string, PuckRoute>(
      routesQuery.data.map((r) => [r.lang, r]),
    );
    const next: Record<string, RouteDraft> = {};
    for (const l of websiteLanguages) {
      const r = byLang.get(l);
      next[l] = {
        slug: r?.slug ?? "",
        title: (r?.seo?.title as string | undefined) ?? "",
        description: (r?.seo?.description as string | undefined) ?? "",
        keywords: (r?.seo?.keywords as string | undefined) ?? "",
      };
    }
    setDrafts(next);
  }, [routesQuery.data, websiteLanguages]);

  const draft = drafts[lang] ?? emptyDraft();

  const setDraft = (patch: Partial<RouteDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [lang]: { ...(prev[lang] ?? emptyDraft()), ...patch },
    }));

  const suggestedSlug = useMemo(() => slugify(pageName), [pageName]);

  const saveMutation = useMutation({
    mutationFn: () =>
      upsertPageRoute(pageId, lang, {
        slug: isHome ? "" : draft.slug,
        seo: {
          title: draft.title,
          description: draft.description,
          keywords: draft.keywords,
        },
      }),
    onSuccess: (routes) => {
      queryClient.setQueryData(routesKey(pageId), routes);
      toast({ title: cmsNew("cms.new.auto.title.seo_enregistre", "SEO enregistré"), variant: "success", duration: 1400 });
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      toast({
        title:
          status === 409
            ? cmsNew("cms.new.seo.slugTaken", "Ce slug est déjà utilisé pour cette langue")
            : status === 400
              ? cmsNew("cms.new.seo.slugInvalid", "Slug invalide pour cette page")
              : cmsNew("cms.new.seo.saveFailed", "Échec de l'enregistrement du SEO"),
        variant: "error",
        duration: 3500,
      });
    },
  });

  const loading = langsLoading || routesQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            SEO — {pageName || cmsPhrase(cmsNew, "unnamedPage")}
            {isHome && (
              <Badge variant="default" className="gap-1">
                <Home size={12} /> {cmsPhrase(cmsNew, "homeBadge")}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {cmsNew("cms.new.seo.dialogDescription", "Titre, description et mots-clés par langue, ainsi que l'URL (slug) de la page.")}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400">
            {cmsNew("cms.new.seo.loading", "Chargement…")}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Sélecteur de langue */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{cmsNew("cms.new.auto.text.langue", "Langue")}</span>
              <MultilingualSelect
                values={websiteLanguages}
                value={lang}
                onValueChange={(v) => setLang(v as SupportedLocale)}
              />
            </div>

            {/* Slug — composeur multi-segments */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="seo-slug">
                  {cmsNew("cms.new.auto.text.slug_url", "Slug (URL)")}
                </Label>
                {!isHome ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    title={cmsNew(
                      "cms.new.auto.title.regenerer_le_slug_a_partir_du_nom_de_la_page",
                      "Régénérer le slug à partir du nom de la page",
                    )}
                    disabled={!suggestedSlug}
                    onClick={() => setDraft({ slug: suggestedSlug })}
                  >
                    <RotateCcw size={14} className="mr-1" />
                    {cmsNew("cms.new.seo.path.resetFromName", "Depuis le nom")}
                  </Button>
                ) : null}
              </div>
              <SlugPathComposer
                lang={lang}
                value={isHome ? "" : draft.slug}
                onChange={(slug) => setDraft({ slug })}
                disabled={isHome}
                placeholder={
                  isHome
                    ? cmsPhrase(cmsNew, "seoRootSlug")
                    : suggestedSlug || cmsPhrase(cmsNew, "seoDefaultSlug")
                }
              />
              {isHome && (
                <p className="text-xs text-slate-500">
                  {cmsNew("cms.new.seo.homeSlugHintBefore", "Page d'accueil : l'URL est la racine du site (« / » ou « /")}
                  {lang}
                  {cmsNew("cms.new.seo.homeSlugHintAfter", " »), le slug n'est pas modifiable.")}
                </p>
              )}
            </div>

            {/* Titre */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="seo-title">{cmsNew("cms.new.auto.text.titre", "Titre")}</Label>
              <Input
                id="seo-title"
                value={draft.title}
                placeholder={cmsNew("cms.new.auto.placeholder.titre_de_longlet_seo", "Titre de l'onglet / SEO")}
                onChange={(e) => setDraft({ title: e.target.value })}
              />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="seo-description">{cmsNew("cms.new.auto.text.description", "Description")}</Label>
              <Textarea
                id="seo-description"
                rows={3}
                value={draft.description}
                placeholder={cmsNew("cms.new.auto.placeholder.meta_description_150_160_caracteres", "Méta-description (≈ 150–160 caractères)")}
                onChange={(e) => setDraft({ description: e.target.value })}
              />
            </div>

            {/* Mots-clés */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="seo-keywords">{cmsNew("cms.new.auto.text.mots_cles", "Mots-clés")}</Label>
              <Input
                id="seo-keywords"
                value={draft.keywords}
                placeholder={cmsNew("cms.new.auto.placeholder.mot1_mot2_mot3", "mot1, mot2, mot3")}
                onChange={(e) => setDraft({ keywords: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {cmsPhrase(cmsNew, "galleryClose")}
              </Button>
              <Button
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {cmsNew("cms.new.seo.save", "Enregistrer")} ({lang.toUpperCase()})
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SeoEditor;
